import { Controller } from 'react-hook-form';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Rating,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { PaymentRating } from '@constants/various';
import { IRatingVisibility } from '@th-types/rating.type';
import { IThJobAssignmentData } from '@worker/types/th-job-assignment.type';
import IRateCompanyCreation from '@worker/types/rate-company-creation.type';
import {
  RATE_COMPANY_MAX_COMMENTS,
  getRateCompanyMode,
  useRateCompanyForm,
  type RateCompanyFormValues,
} from './useRateCompanyForm';
import { useRateCompanyMutation } from './useRateCompanyMutation';

interface RateCompanyFormProps {
  jobAssignment: IThJobAssignmentData;
  onSuccess: () => void;
  onCancel: () => void;
}

const readOnlyRatingStyles = {
  root: { opacity: 0.4, cursor: 'not-allowed' },
  symbolGroup: { pointerEvents: 'none' },
} as const;

const paymentOnlyInputStyles = {
  input: { opacity: 1, color: 'var(--mantine-color-text)' },
} as const;

const paymentOnlyCheckboxStyles = {
  label: { color: 'var(--mantine-color-text)' },
} as const;

function buildBody(values: RateCompanyFormValues): IRateCompanyCreation {
  return {
    communication: values.communication,
    atEventManagement: values.atEventManagement,
    professionalism: values.professionalism,
    comments: values.comments,
    improvementComments: values.improvementComments,
    payment: values.payment,
    visibility: values.visibility
      ? IRatingVisibility.PUBLIC
      : IRatingVisibility.PRIVATE,
  };
}

export default function RateCompanyForm({
  jobAssignment,
  onSuccess,
  onCancel,
}: RateCompanyFormProps) {
  const mode = getRateCompanyMode(jobAssignment);
  const isPaymentOnly = mode === 'PAYMENT_ONLY';

  const form = useRateCompanyForm(jobAssignment);
  const { control, handleSubmit, formState } = form;

  const mutation = useRateCompanyMutation({ onSuccess });

  const companyName = jobAssignment.job?.company?.name ?? '';
  const companyLogo = jobAssignment.job?.company?.profileImageUrl;

  const onSubmit = (values: RateCompanyFormValues) => {
    mutation.mutate({
      jobAssignmentId: jobAssignment.id,
      mode,
      body: buildBody(values),
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <Stack gap="md" p="md" style={{ flex: 1, overflowY: 'auto' }}>
        <Group wrap="nowrap" align="center">
          <Avatar src={companyLogo} radius="md" size="lg" alt={companyName} />
          <Stack gap={2}>
            <Title order={5}>{companyName || 'Rate Company'}</Title>
            <Text c="dimmed" size="sm">
              {isPaymentOnly
                ? 'Update your payment timing for this rating.'
                : 'This review will be shared publicly with your name and photo.'}
            </Text>
          </Stack>
        </Group>

        <Divider />

        <Controller
          control={control}
          name="comments"
          render={({ field, fieldState }) => (
            <Textarea
              label="What did this company do really well?"
              placeholder={
                isPaymentOnly
                  ? undefined
                  : 'They were amazing at X, did Y for the team.'
              }
              autosize
              minRows={3}
              maxLength={RATE_COMPANY_MAX_COMMENTS}
              disabled={isPaymentOnly}
              styles={isPaymentOnly ? paymentOnlyInputStyles : undefined}
              error={fieldState.error?.message}
              description={`${field.value?.length ?? 0}/${RATE_COMPANY_MAX_COMMENTS}`}
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="payment"
          render={({ field, fieldState }) => (
            <Stack gap="xs">
              <Text fw={600} size="sm">
                How long did it take for you to get paid? *
              </Text>
              <SimpleGrid cols={2} spacing="xs">
                {PaymentRating.map((option) => {
                  const isSelected = field.value === option.index;
                  return (
                    <Button
                      key={`payment-${option.index ?? 'none'}`}
                      type="button"
                      variant={isSelected ? 'filled' : 'default'}
                      size="sm"
                      onClick={() => field.onChange(option.index)}
                    >
                      {option.title}
                    </Button>
                  );
                })}
              </SimpleGrid>
              {fieldState.error?.message && (
                <Text c="red" size="xs">
                  {fieldState.error.message}
                </Text>
              )}
            </Stack>
          )}
        />

        <Stack gap="xs">
          <Text fw={600} size="sm">
            Rate this company
          </Text>
          <SimpleGrid cols={3} spacing="xs">
            <Controller
              control={control}
              name="communication"
              render={({ field, fieldState }) => (
                <Stack gap={4} align="center">
                  <Text size="xs" c="dimmed">
                    COMMUNICATION
                  </Text>
                  <Rating
                    size="lg"
                    value={field.value}
                    onChange={field.onChange}
                    readOnly={isPaymentOnly}
                    styles={isPaymentOnly ? readOnlyRatingStyles : undefined}
                  />
                  {fieldState.error?.message && (
                    <Text c="red" size="xs" ta="center">
                      Required
                    </Text>
                  )}
                </Stack>
              )}
            />
            <Controller
              control={control}
              name="atEventManagement"
              render={({ field, fieldState }) => (
                <Stack gap={4} align="center">
                  <Text size="xs" c="dimmed">
                    AT EVENT
                  </Text>
                  <Rating
                    size="lg"
                    value={field.value}
                    onChange={field.onChange}
                    readOnly={isPaymentOnly}
                    styles={isPaymentOnly ? readOnlyRatingStyles : undefined}
                  />
                  {fieldState.error?.message && (
                    <Text c="red" size="xs" ta="center">
                      Required
                    </Text>
                  )}
                </Stack>
              )}
            />
            <Controller
              control={control}
              name="professionalism"
              render={({ field, fieldState }) => (
                <Stack gap={4} align="center">
                  <Text size="xs" c="dimmed">
                    PROFESSIONALISM
                  </Text>
                  <Rating
                    size="lg"
                    value={field.value}
                    onChange={field.onChange}
                    readOnly={isPaymentOnly}
                    styles={isPaymentOnly ? readOnlyRatingStyles : undefined}
                  />
                  {fieldState.error?.message && (
                    <Text c="red" size="xs" ta="center">
                      Required
                    </Text>
                  )}
                </Stack>
              )}
            />
          </SimpleGrid>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text fw={600} size="sm">
            Optional — What can this company improve?
          </Text>
          <Text size="xs" c="dimmed">
            Your name and picture appear with any public review. You can keep
            improvement comments private or check the box to share them
            publicly.
          </Text>
          <Controller
            control={control}
            name="improvementComments"
            render={({ field, fieldState }) => (
              <Textarea
                placeholder={
                  isPaymentOnly
                    ? undefined
                    : 'Share something valuable to help a company improve.'
                }
                autosize
                minRows={3}
                maxLength={RATE_COMPANY_MAX_COMMENTS}
                disabled={isPaymentOnly}
                styles={isPaymentOnly ? paymentOnlyInputStyles : undefined}
                error={fieldState.error?.message}
                description={`${field.value?.length ?? 0}/${RATE_COMPANY_MAX_COMMENTS}`}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="visibility"
            render={({ field }) => (
              <Checkbox
                label="Agree to share improvement publicly with name and picture."
                checked={field.value}
                onChange={(event) =>
                  field.onChange(event.currentTarget.checked)
                }
                disabled={isPaymentOnly}
                styles={isPaymentOnly ? paymentOnlyCheckboxStyles : undefined}
              />
            )}
          />
        </Stack>
      </Stack>

      <Group justify="end" p="md">
        <Button variant="light" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button
          type="submit"
          loading={mutation.isPending}
          disabled={!formState.isValid && formState.isSubmitted}
        >
          {isPaymentOnly ? 'Save payment info' : 'Submit rating'}
        </Button>
      </Group>
    </Box>
  );
}
